import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Utils
import { slugToDocument, parseLegalDocumentVersion } from '@repo/front-end-utils';

// Services
import { getLegalDocument } from '@repo/front-end-services';
import type { LegalDocumentContent } from '@repo/front-end-services';

// Components
import { LegalDocumentView, TLSpinnerPage } from '@repo/ui';

// Pages
import NotFoundPage from '../NotFoundPage';
import SomethingWentWrongPage from '../SomethingWentWrongPage';

// Data
import Config from '../../common/Config';

/**
 * Public, un-authenticated legal-document reader (spec-twin-viewer-legal-pages). Serves both
 * `/legal/:slug` (current version) and `/legal/:slug/:version` (a specific published version)
 * from the DB-held registry via the middleware. twin-viewer is a CRA app with no SSR, so unlike
 * the `apps/twin` server-rendered pages the fetch runs client-side in an effect.
 *
 * NOT behind the protected gate (FR7): both linkers (the legal gate and the re-consent notice
 * bell) open these in a new tab, where the gate would race the Auth0 context and
 * redirect to login; the backend endpoint is itself unauthenticated.
 */

type FetchState =
  | { status: 'loading' }
  | { status: 'ready'; doc: LegalDocumentContent }
  | { status: 'notfound' }
  | { status: 'error' };

const LegalDocumentPage: React.FC = () => {
  const { slug, version } = useParams<{ slug: string; version?: string }>();

  const documentKey = slug ? slugToDocument(slug) : null;
  // A version segment, when present, must be a canonical positive integer; otherwise not-found.
  const parsedVersion = version === undefined ? undefined : parseLegalDocumentVersion(version);
  const versionInvalid = version !== undefined && parsedVersion === null;
  // Unknown slug or non-canonical version is a terminal not-found, decided synchronously — no fetch.
  const resolvable = documentKey !== null && !versionInvalid;

  const [state, setState] = useState<FetchState>(() =>
    resolvable ? { status: 'loading' } : { status: 'notfound' },
  );

  // react-router reuses this component instance across /legal/* navigations, so the resolved
  // target changing must reset state synchronously *during render* — otherwise the previous
  // document would render for one frame paired with the new URL (a content/route mismatch), or a
  // deterministically-not-found URL would flash the spinner. Adjusting state during render on a
  // key change is React's endorsed pattern for this (cheaper than an effect + no visible flicker).
  const targetKey = resolvable ? `${documentKey}@${parsedVersion ?? 'current'}` : 'notfound';
  const [renderedKey, setRenderedKey] = useState(targetKey);
  if (renderedKey !== targetKey) {
    setRenderedKey(targetKey);
    setState(resolvable ? { status: 'loading' } : { status: 'notfound' });
  }

  useEffect(() => {
    if (!resolvable) return; // not-found already set synchronously above (no network hit)

    let active = true;
    getLegalDocument(Config.middlewareApi, documentKey, parsedVersion ?? undefined)
      .then(doc => {
        if (!active) return;
        // null → 404 / no current version / malformed body → not-found (FR6).
        setState(doc ? { status: 'ready', doc } : { status: 'notfound' });
      })
      .catch(() => {
        // Only a network/5xx/timeout failure throws → something-went-wrong (FR6).
        if (active) setState({ status: 'error' });
      });

    return () => {
      active = false;
    };
  }, [documentKey, parsedVersion, resolvable]);

  switch (state.status) {
    case 'loading':
      return <TLSpinnerPage />;
    case 'notfound':
      return <NotFoundPage />;
    case 'error':
      return <SomethingWentWrongPage />;
    case 'ready':
      // slug is defined here (documentKey resolved from it), but guard for the type-checker.
      return <LegalDocumentView doc={state.doc} slug={slug ?? ''} />;
  }

  // Unreachable today (the switch covers every FetchState). Kept as a graceful fallback so a
  // future state variant added without a case degrades to the error page rather than a blank
  // render (FR6 — never blank/crash).
  return <SomethingWentWrongPage />;
};

export default LegalDocumentPage;
